import { BybButton } from "@Components/common/byb-ds";
import CenteredLoadingOverlay from "@Components/generic/centered-loading-overlay";
import Icon from "@Components/generic/icon";
import PlayPositions from "@Enums/PlayPositions";
import { faInfoCircle, faPlusCircle } from "@fortawesome/pro-solid-svg-icons";
import { useApi, useGlobalContext } from "@hooks";
import useI18n from "@i18n";
import { Stack, Typography, styled, useTheme } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Paper from "@mui/material/Paper";
import { Api } from "@Shared/schemas";
import {
  CreatePlaylistRequest,
  PlaylistResponse,
  PlaylistScenario,
  PlaylistScenarioConfig,
  UpdatePlaylistRequest,
} from "@Shared/schemas/playlists/playlistSchemas";
import { ScenarioResponse } from "@Shared/schemas/scenarios/scenarioSchemas";
import { Reorder } from "framer-motion";
import { useEffect, useState } from "react";
import { DialogSection } from "../../common";
import { StyledDialogActions, StyledDialogTitle } from "../../common/dialog/styled-components";
import PlaylistDetails from "./playlist-details";
import PlaylistScenarioListItem from "./playlist-scenario-reorder-list-item";
import ScenarioSettings from "./playlist-scenario-settings";
import PlaylistScenarioSelectionDialog from "./scenario-selection/playlist-scenario-selection-dialog";

const mockScenarios: any = [
  {
    name: "Last-Minute Equilizer",
    phase_of_play: "Counter attack",
    play_positions: [PlayPositions.DEFENSIVE_MIDFIELDER],
  },
  {
    name: "Midfield Maestro Challenge",
    phase_of_play: "Build up",
    play_positions: [PlayPositions.ATTACKING_MIDFIELDER],
  },
  {
    name: "Defensive Set-Piece Drill",
    phase_of_play: "Build up",
    play_positions: [PlayPositions.DEFENSIVE_MIDFIELDER, PlayPositions.ATTACKING_MIDFIELDER],
  },
  {
    name: "Forward's Finishing Exercise",
    phase_of_play: "Counter attack",
    play_positions: [PlayPositions.RIGHT_WINGER],
  },
  {
    name: "Winger's Cross Accuracy Test",
    phase_of_play: "Counter attack",
    play_positions: [PlayPositions.RIGHT_WINGER],
  },
  {
    name: "Strikers Do's & Dont's ",
    phase_of_play: "Build up",
    play_positions: [PlayPositions.STRIKER],
  },
];

/**
 * This component is part of the VR app V2 redesign.
 * It is composed differently than what you would normally do for a dialog becuase we are
 * dealing with two dialogs in one.
 */

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "stretch",
  gap: theme.spacing(3),
  minHeight: "80vh",
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  "&.MuiPaper-root": {
    border: "1px solid",
    borderColor: theme.palette.dsSurface.v200,
    borderRadius: theme.spacing(3),
    backgroundColor: theme.palette.dsBase.v100,
    flex: 1,
    minWidth: theme.spacing(70),
  },
}));

const DialogSectionWrapper = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(3),
  overflow: "scroll",
  flex: 1,
}));

interface IPlaylistDialog {
  open: boolean;
  title?: React.ReactNode;
  onClose: (refresh?: boolean) => void;
  playlist?: PlaylistResponse;
  initialScenarios?: ScenarioResponse[];
}

// Add this interface to extend PlaylistScenario with a unique ID
interface PlaylistScenarioWithId extends PlaylistScenario {
  uniqueId: string;
  isNewAddition?: boolean;
}

// Add this interface before the PlaylistDialog component
interface ExtendedPlaylistDetails extends Partial<CreatePlaylistRequest> {
  _userRecords?: any[];
  _teamRecords?: any[];
  _accountRecords?: any[];
}

interface PlaylistScenarioResponse {
  scenario_id: number;
  scenario: ScenarioResponse;
  game_speed?: number | null;
  bullet_time_decisions?: boolean | number | null;
  memory_player_count?: number | null;
  memory_time_limit?: number | null;
  memory_distance?: number | null;
  mirrored?: boolean | number | null;
  index?: number | null;
}

const PlaylistDialog = ({ open, title, onClose, playlist, initialScenarios }: IPlaylistDialog) => {
  const theme = useTheme();
  const { i18n } = useI18n();
  const { globalState } = useGlobalContext();
  const [scenarios, setScenarios] = useState<PlaylistScenarioWithId[]>([]);
  const [addScenariosDialogOpen, setAddScenariosDialogOpen] = useState(false);
  const [globalConfig, setGlobalConfig] = useState<Partial<PlaylistScenarioConfig>>({});
  const [scenarioConfigs, setScenarioConfigs] = useState<{ [key: string]: Partial<PlaylistScenarioConfig> }>({});
  const [playlistDetails, setPlaylistDetails] = useState<ExtendedPlaylistDetails>({
    name: "",
    description: "",
    published: true,
    shared_with_accounts: [],
    tags: [],
  });
  const [error, setError] = useState<string | null>(null);

  const { call: createPlaylist, loading: isCreating } = useApi<typeof Api.Playlists.CreatePlaylist>({
    api: Api.Playlists.CreatePlaylist,
    onError: (error) => {
      setError(error.message);
    },
  });

  const { call: updatePlaylist, loading: isUpdating } = useApi<typeof Api.Playlists.UpdatePlaylist>({
    api: Api.Playlists.UpdatePlaylist,
    onError: (error) => {
      setError(error.message);
    },
  });

  const { call: getScenarios, loading: getScenariosLoading } = useApi<typeof Api.Scenarios.GetScenarios>({
    api: Api.Scenarios.GetScenarios,
  });
  const { call: getPlaylistScenarios, loading: getPlaylistScenariosLoading } = useApi<any>({
    api: (Api.Scenarios as any).GetPlaylistScenarios,
  });

  useEffect(() => {
    let isCancelled = false;

    const loadPlaylistScenarios = async () => {
      if (playlist) {
        setPlaylistDetails({
          name: playlist.name,
          description: playlist.description,
          start_at: playlist.start_at,
          end_at: playlist.end_at,
          tags: playlist.tags?.map((tag) => tag.tag) || [],
          shared_with_users: playlist.shared_users?.map((u) => u.id) || [],
          shared_with_teams: playlist.shared_teams?.map((t) => t.id) || [],
          shared_with_accounts: playlist.shared_accounts?.map((a) => a.id) || [],
          published: playlist.published,
          _userRecords:
            playlist.shared_users?.map((u) => ({
              id: u.id,
              first_name: u.name.split(" ")[0] || "",
              last_name: u.name.split(" ").slice(1).join(" ") || "",
              email: "",
              created_at: u.created_at,
            })) || [],
          _teamRecords:
            playlist.shared_teams?.map((t) => ({
              id: t.id,
              name: t.name,
              created_at: t.created_at,
            })) || [],
          _accountRecords:
            playlist.shared_accounts?.map((a) => ({
              id: a.id,
              name: a.name,
              created_at: a.created_at,
              members: [],
            })) || [],
        });

        setGlobalConfig({
          game_speed: playlist.game_speed ?? undefined,
          bullet_time_decisions: playlist.bullet_time_decisions != null ? Boolean(playlist.bullet_time_decisions) : undefined,
          memory_player_count: playlist.memory_player_count ?? undefined,
          memory_time_limit: playlist.memory_time_limit ?? undefined,
          memory_distance: playlist.memory_distance ?? undefined,
          mirrored: playlist.mirrored != null ? Boolean(playlist.mirrored) : undefined,
        });

        const response = await getPlaylistScenarios({
          account_id: globalState.account?.id! as number,
          include_public: true,
          playlist_id: playlist.id,
        });

        if (isCancelled) {
          return;
        }

        const existingPlaylistScenarios =
          response.code === "ok"
            ? (response.data.scenarios as PlaylistScenarioResponse[]).map((playlistScenario, index) => ({
                  scenario: playlistScenario.scenario,
                  scenario_id: playlistScenario.scenario_id,
                  game_speed: playlistScenario.game_speed ?? null,
                  bullet_time_decisions:
                    playlistScenario.bullet_time_decisions === null || playlistScenario.bullet_time_decisions === undefined
                      ? null
                      : Boolean(playlistScenario.bullet_time_decisions),
                  memory_player_count: playlistScenario.memory_player_count ?? null,
                  memory_time_limit: playlistScenario.memory_time_limit ?? null,
                  memory_distance: playlistScenario.memory_distance ?? null,
                  mirrored:
                    playlistScenario.mirrored === null || playlistScenario.mirrored === undefined
                      ? null
                      : Boolean(playlistScenario.mirrored),
                  index: playlistScenario.index ?? index,
                  uniqueId: `${playlistScenario.scenario_id}-${Date.now()}-${index}`,
                  isNewAddition: false,
                }))
            : [];

        if (initialScenarios?.length) {
          const newScenarios = initialScenarios.filter(
            (newScenario) => !existingPlaylistScenarios.some((existing) => existing.scenario.id === newScenario.id)
          );

          const mappedNewScenarios = newScenarios.map((scenario, index) => ({
            scenario,
            scenario_id: scenario.id,
            game_speed: null,
            bullet_time_decisions: null,
            memory_player_count: null,
            memory_time_limit: null,
            memory_distance: null,
            mirrored: null,
            index: existingPlaylistScenarios.length + index,
            uniqueId: `${scenario.id}-${Date.now()}-new-${index}`,
            isNewAddition: true,
          }));

          setScenarios([...existingPlaylistScenarios, ...mappedNewScenarios]);
        } else {
          setScenarios(existingPlaylistScenarios);
        }

        return;
      }

      if (initialScenarios?.length) {
        setScenarios(
          initialScenarios.map((scenario, index) => ({
            scenario,
            scenario_id: scenario.id,
            game_speed: null,
            bullet_time_decisions: null,
            memory_player_count: null,
            memory_time_limit: null,
            memory_distance: null,
            mirrored: null,
            index,
            uniqueId: `${scenario.id}-${Date.now()}-${index}`,
            isNewAddition: true,
          }))
        );
      } else {
        setScenarios([]);
      }
    };

    loadPlaylistScenarios();

    return () => {
      isCancelled = true;
    };
  }, [globalState.account?.id, initialScenarios, playlist]);

  const handleSave = async () => {
    if (!playlistDetails) return;

    // Apply global config to all scenarios that don't have specific overrides
    const configuredScenarios = scenarios.map((item) => ({
      scenario_id: item.scenario.id,
      ...globalConfig,
      ...scenarioConfigs[item.uniqueId],
      game_speed: scenarioConfigs[item.uniqueId]?.game_speed ?? globalConfig.game_speed,
      bullet_time_decisions: scenarioConfigs[item.uniqueId]?.bullet_time_decisions ?? globalConfig.bullet_time_decisions,
      memory_player_count: scenarioConfigs[item.uniqueId]?.memory_player_count ?? globalConfig.memory_player_count,
      memory_time_limit: scenarioConfigs[item.uniqueId]?.memory_time_limit ?? globalConfig.memory_time_limit,
      memory_distance: scenarioConfigs[item.uniqueId]?.memory_distance ?? globalConfig.memory_distance,
      mirrored: scenarioConfigs[item.uniqueId]?.mirrored ?? globalConfig.mirrored,
      index: item.index,
    })) as PlaylistScenarioConfig[];

    const baseRequest = {
      name: playlistDetails.name || "",
      description: playlistDetails.description,
      start_at: playlistDetails.start_at,
      end_at: playlistDetails.end_at,
      published: playlistDetails.published || false,
      archived: playlistDetails.archived || false,
      scenarios: configuredScenarios,
      game_speed: globalConfig.game_speed,
      bullet_time_decisions: globalConfig.bullet_time_decisions,
      memory_player_count: globalConfig.memory_player_count,
      memory_time_limit: globalConfig.memory_time_limit,
      memory_distance: globalConfig.memory_distance,
      mirrored: globalConfig.mirrored,
    };

    try {
      if (playlist) {
        // Update existing playlist
        const updateRequest: UpdatePlaylistRequest = {
          id: playlist.id,
          ...baseRequest,
          shared_with_users: playlistDetails.shared_with_users || [],
          shared_with_teams: playlistDetails.shared_with_teams || [],
          shared_with_accounts: playlistDetails.shared_with_accounts || [],
        };
        await updatePlaylist(updateRequest);
      } else {
        // Create new playlist
        const createRequest: CreatePlaylistRequest = {
          ...baseRequest,
          account_id: globalState.account?.id || 0,
          shared_with_users: playlistDetails.shared_with_users || [],
          shared_with_teams: playlistDetails.shared_with_teams || [],
          shared_with_accounts: playlistDetails.shared_with_accounts || [],
        };
        await createPlaylist(createRequest);
      }
      onClose(true);
    } catch (error) {
      console.error(i18n("playlists.list.save.error"), error);
      setError(error instanceof Error ? error.message : i18n("playlists.list.save.error"));
    }
  };

  const handleDetailsChange = (details: Partial<CreatePlaylistRequest>) => {
    setPlaylistDetails((prev) => ({
      ...prev,
      ...details,
    }));
  };

  const handleScenarioConfigChange = (uniqueId: string, config: Partial<PlaylistScenarioConfig>) => {
    setScenarioConfigs((prev) => ({
      ...prev,
      [uniqueId]: config,
    }));
  };

  const handleScenarioSelect = async (selectedScenarios: ScenarioResponse[]) => {
    // Get scenario IDs from selected scenarios
    const scenarioIds = selectedScenarios.map((scenario) => scenario.id);

    // Fetch all scenarios at once using getScenarios
    const response = await getScenarios({
      account_id: globalState.account?.id! as number,
      scenario_ids: scenarioIds,
      include_public: true,
    });

    if (response.code === "ok" && response.data) {
      // Map the fetched scenarios to the required format
      const newScenarios = response.data.scenarios.map((scenario) => {
        // Find the highest index for this scenario to support duplicates
        const existingIndices = scenarios
          .filter((s) => s.scenario.id === scenario.id)
          .map((s) => s.index)
          .filter((index): index is number => index !== null && index !== undefined);

        const nextIndex = existingIndices.length > 0 ? Math.max(...existingIndices) + 1 : scenarios.length;

        return {
          scenario,
          scenario_id: scenario.id,
          game_speed: null,
          bullet_time_decisions: null,
          memory_player_count: null,
          memory_time_limit: null,
          memory_distance: null,
          mirrored: null,
          index: nextIndex,
          uniqueId: `${scenario.id}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          isNewAddition: true,
        };
      });

      setScenarios((prev) => [...prev, ...newScenarios]);
    }
    setAddScenariosDialogOpen(false);
  };

  const handleReorder = (newScenarios: PlaylistScenarioWithId[]) => {
    // Create a map of existing scenarios for quick lookup by uniqueId
    const existingScenarios = scenarios.reduce((acc, scenario) => {
      acc[scenario.uniqueId] = scenario;
      return acc;
    }, {} as { [key: string]: PlaylistScenarioWithId });

    // Map the reordered items to maintain all properties while updating index
    const reorderedScenarios = newScenarios.map((scenario, index) => ({
      ...existingScenarios[scenario.uniqueId],
      index,
    }));

    setScenarios(reorderedScenarios);

    // Update configs to maintain the same order
    const newConfigs = reorderedScenarios.reduce((acc, scenario) => {
      acc[scenario.uniqueId] = {
        ...scenarioConfigs[scenario.uniqueId],
        index: scenario.index,
      };
      return acc;
    }, {} as { [key: string]: Partial<PlaylistScenarioConfig> });

    setScenarioConfigs(newConfigs);
  };

  const isValid = playlistDetails.name && scenarios.length > 0;

  const isLoading = isCreating || isUpdating || getScenariosLoading || getPlaylistScenariosLoading;

  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      aria-labelledby="dual-dialog-title"
      sx={{
        "& .MuiPaper-root": {
          backgroundColor: "transparent",
          boxShadow: "none",
          maxWidth: "unset",
        },
      }}
    >
      <StyledDialogContent>
        {isLoading && <CenteredLoadingOverlay loading={isLoading} />}
        {/* Playlist details */}
        <StyledPaper>
          <Stack sx={{ height: "100%" }}>
            <StyledDialogTitle id="playlist-dialog-title">
              {typeof title === "string" ? (
                <Typography variant="text2xl700" color="dsBase.v200">
                  {title}
                </Typography>
              ) : (
                title
              )}
            </StyledDialogTitle>

            <DialogSectionWrapper>
              <PlaylistDetails details={playlistDetails} onChange={handleDetailsChange} />
            </DialogSectionWrapper>

            <StyledDialogActions>
              <BybButton onClick={() => onClose()} color="secondary" fullWidth>
                Close
              </BybButton>
              <BybButton onClick={handleSave} color="primary" fullWidth disabled={!isValid || isCreating || isUpdating}>
                {playlist ? i18n("playlists.playlist.page.update") : i18n("playlists.playlist.page.create")}
              </BybButton>
            </StyledDialogActions>
          </Stack>
        </StyledPaper>

        {/* Scenarios */}
        <StyledPaper>
          <Stack sx={{ height: "100%" }}>
            <DialogSectionWrapper>
              <Stack spacing={4}>
                {scenarios.length > 0 && (
                  <DialogSection>
                    <ScenarioSettings config={globalConfig} onChange={setGlobalConfig} />
                  </DialogSection>
                )}
                <DialogSection title={`Scenarios (${scenarios.length})`}>
                  {scenarios.length > 0 ? (
                    <Reorder.Group axis="y" values={scenarios} onReorder={handleReorder} style={{ padding: 0, position: "relative" }}>
                      {scenarios.map((item) => (
                        <PlaylistScenarioListItem
                          key={item.uniqueId}
                          value={item}
                          onConfigChange={(config) => handleScenarioConfigChange(item.uniqueId, config)}
                          config={
                            scenarioConfigs[item.uniqueId] || {
                              game_speed: item.game_speed,
                              bullet_time_decisions: item.bullet_time_decisions,
                              memory_player_count: item.memory_player_count,
                              memory_time_limit: item.memory_time_limit,
                              memory_distance: item.memory_distance,
                              mirrored: item.mirrored,
                              index: item.index,
                            }
                          }
                          onDelete={() => setScenarios((prev) => prev.filter((s) => s.uniqueId !== item.uniqueId))}
                          isNewAddition={item.isNewAddition}
                        />
                      ))}
                    </Reorder.Group>
                  ) : (
                    <Stack
                      spacing={1}
                      sx={{
                        justifyContent: "center",
                        alignItems: "center",
                        flex: 1,
                        height: "100%",
                        minHeight: theme.spacing(40),
                        py: theme.spacing(4),
                      }}
                    >
                      <Icon icon={faInfoCircle} color={theme.palette.dsNeutral.v400} size="2x" />
                      <Typography variant="textLg500" color="dsNeutral.v400">
                        {i18n("playlists.playlist.page.no-scenarios")}
                      </Typography>
                    </Stack>
                  )}
                </DialogSection>
              </Stack>
            </DialogSectionWrapper>

            <StyledDialogActions>
              <BybButton
                onClick={() => setAddScenariosDialogOpen(true)}
                color="secondary"
                fullWidth
                icon={<Icon icon={faPlusCircle} color={theme.palette.dsNeutral.v400} />}
              >
                <Typography variant="textLg600" color="dsNeutral.v400">
                  Add scenarios
                </Typography>
              </BybButton>
            </StyledDialogActions>

            <PlaylistScenarioSelectionDialog
              onClose={() => setAddScenariosDialogOpen(false)}
              open={addScenariosDialogOpen}
              onSelect={handleScenarioSelect}
              existingScenarios={scenarios}
            />
          </Stack>
        </StyledPaper>
      </StyledDialogContent>
    </Dialog>
  );
};

export default PlaylistDialog;
